import { useEffect, useRef, useState, type ReactNode } from "react";

interface RevealProps {
  children: ReactNode;
  index?: number;
  className?: string;
}

/**
 * Scroll-reveal wrapper: fades/slides children in when they enter the viewport.
 * Purely visual — no layout shift, content is visible without JS, and the
 * animation is disabled under prefers-reduced-motion (see .reveal in index.css).
 */
export function Reveal({ children, index = 0, className = "" }: RevealProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [entered, setEntered] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || !window.IntersectionObserver) {
      setEntered(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return;
        setEntered(true);
        observer.disconnect();
      },
      { threshold: 0.12, rootMargin: "40px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`reveal ${entered ? "reveal-entered" : ""} ${className}`}
      style={{ "--reveal-delay": `${Math.min(index * 70, 350)}ms` } as React.CSSProperties}
    >
      {children}
    </div>
  );
}
